import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Select from 'react-select-legacy';
import { isEmpty, includes } from 'lodash';
import { ASQLogos, formatDate, formatDateUTC, columnName } from 'utils/Formatters';
import { isTeachersEnabled } from 'utils/authScopes';
import { hasASQCompleteFeature } from 'utils/features';
import { linkWithParams } from 'routes';
import PlayerSection from './PlayerSection';
import PlayerSurveyResultsParentEmailSendout from './PlayerSurveyResultsParentEmailSendout';
import PlayerSurveyReminderSendout from './PlayerSurveyReminderSendout';
import classNames from 'classnames';
import localeNames from 'utils/localeNames.jsx.erb'
import Modal from 'react-responsive-modal';
import { closeIconPath } from '../../containers/modals/ModalRoot';
import { FollowUpParentMessage, LastFollowUpParentMessageSendout } from '../followup/FollowUpParentMessage';

const STATE_ICONS = {
  'completed': 'sp-icon-checkmark',
  'overdue': 'sp-icon-danger',
}

const stateIcon = s => {
  if (!s) return null;
  switch (s.state) {
    case 'completed':
      return (
        <span className={`cs-txt-link--sp-${s.state} u-flex-inline align-middle`}>
          <i className={`${STATE_ICONS[s.state]} o-player-section__icon`}></i>
          <span className='u-txt--titleize'><b>Completed</b></span>
        </span>);
    case 'overdue':
      return (
        <span className={`cs-txt-link--sp-${s.state} u-flex-inline align-middle`}>
          <i className={`${STATE_ICONS[s.state]} o-player-section__icon`}></i>
          <span className='u-txt--titleize'><b>Pending</b></span>
        </span>);
    case 'archived':
      return (
        <span className={`cs-txt-link--sp-${s.state} u-flex-inline align-middle`}>
          <span className='u-txt--titleize u-margin-left--small u-padding-left--base'><b>{s.state}</b></span>
        </span>);
    default:
      return (
        <span className='cs-txt-link--sp-black u-flex-inline align-middle'>
          <span className='u-txt--titleize u-margin-left--small u-padding-left--base'><b>Due&nbsp;</b></span>
          {formatDateUTC(s.deadlineDate)}
        </span>);
  }
}

const teacherStateText = (s, playerId, location, withStart = false) => {
  if (!s) return null;
  const startText = s.status === 'pending' ? 'Start Now' : 'Continue';
  const startLink = () => (
    <Link to={{
      pathname: linkWithParams('asq', { player_id: playerId, id: s.id }),
      state: { from: location },
    }}
    className='cs-txt-link--sp-blue'>
      {startText}
    </Link>
  );
  switch (s.state) {
    case 'archived':
      return '';
    case 'completed':
      return <span>{formatDateUTC(s.dateFinished)}</span>;
    case 'overdue':
      return (
        <span>{withStart && startLink()}</span>);
    default:
      return withStart ? <span>{startLink()}</span> :
        <span className='u-txt--titleize'>{s.status === 'pending' ? 'Not Started' : 'In Progress'}</span>;
  }
}

const parentStateText = s => {
  if (!s) return null;
  const startText = s.status === 'pending' ? 'Not Started' : 'In Progress';
  switch (s.state) {
    case 'archived':
      return '';
    case 'completed':
      return <span>{formatDateUTC(s.dateFinished)}</span>;
    default:
      return <span>{startText}</span>;
  }
}

export default class PlayerSurvey extends React.Component {
  state = {
    locale: this.props.player.locale,
    followUpParentMessageOpen: false,
    hasUnsavedTranslation: false,
  }

  static propTypes = {
    auth: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    download: PropTypes.object,
    location: PropTypes.object.isRequired,
    player: PropTypes.object,
    survey: PropTypes.object.isRequired,
    questionnaires: PropTypes.arrayOf(PropTypes.object).isRequired,
    asqSurveyResultsParentEmailSendout: PropTypes.object.isRequired,
    onQuestionInfo: PropTypes.func.isRequired,
    onSurveyChange: PropTypes.func.isRequired,
    onClickNotes: PropTypes.func.isRequired,
    onDownloadASQReport: PropTypes.func,
    onEnqueueAsqSurveyResultsParentEmailSendout: PropTypes.func,
    asqFollowUpParentMessagesEnabled: PropTypes.bool,
  };

  onLocaleChange = (e) => {
    this.setState({ locale: e.value });
  }

  optionRenderer = (opt) => {
    return opt.value === this.props.player.locale ? <b>{opt.label} <span role="img" aria-label="check">✅</span></b> : opt.label
  }

  onClickASQPdf = (e) => {
    e.stopPropagation();
    const { survey, onDownloadASQReport, download } = this.props;
    const parentSurvey = survey.entities.surveys[survey.result.questionnaires.parent];
    if (download.isDownloading) return;
    onDownloadASQReport(parentSurvey.id, { locale: this.state.locale });
  };

  onClickResultsParentEmailSendout = () => {
    const { survey, onEnqueueAsqSurveyResultsParentEmailSendout } = this.props;
    const parentSurvey = survey.entities.surveys[survey.result.questionnaires.parent];
    onEnqueueAsqSurveyResultsParentEmailSendout(parentSurvey.id);
  }

  onClickFollowUpParentMessageOpen = (e) => {
    e.stopPropagation();
    this.setState({ followUpParentMessageOpen: true, hasUnsavedTranslation: false });
  }

  onCloseFollowUpParentMessageOpen = (_e) => {
    if (this.state.hasUnsavedTranslation) {
      if (window.confirm("Are you sure you don't want to send the message?")) {
        this.setState({ followUpParentMessageOpen: false, hasUnsavedTranslation: false });
      }
    } else {
      this.setState({ followUpParentMessageOpen: false });
    }
  }

  onTranslationStateChange = (hasTranslation) => {
    this.setState({ hasUnsavedTranslation: hasTranslation });
  }

  render() {
    const { auth, isLoading, player, download, survey, questionnaires, asqSurveyResultsParentEmailSendout, onQuestionInfo, onSurveyChange, onClickNotes, location, asqFollowUpParentMessagesEnabled } = this.props;
    if (isEmpty(survey)) return null;
    const isManageAsqFollowUp = auth.isManageAsqFollowUp;
    const clsPDF = classNames('o-sp-button o-sp-button--survey-download o-sp-button--sufixed', { 'u-disabled': download.isDownloading });
    const showTeacher = isTeachersEnabled(auth);
    const teacherSurvey = showTeacher ? survey.entities.surveys[survey.result.questionnaires.teacher] : null;
    const parentSurvey = survey.entities.surveys[survey.result.questionnaires.parent];
    const withReport = !isEmpty(parentSurvey.asqReportType);
    const showEmailSendout = includes(auth.options, 'asqResultsParentEmailManualSendoutEnabled') && withReport;
    const locales = Object.entries(localeNames).map(([k,v]) => ({ value: k, label: v }));
    const rows =
      survey.entities.questionnaires[parentSurvey.questionnaire].sections.map(s =>
        <PlayerSection key={`section-${s}`} section={survey.entities.sections[s]}
          entities={survey.entities}
          auth={auth}
          state={teacherSurvey ? teacherSurvey.state : parentSurvey.state}
          teacherSurvey={teacherSurvey}
          onQuestionInfo={onQuestionInfo} onClickNotes={onClickNotes}
          isLoading={isLoading}/>
      );

    return (
      <Fragment>
        <div className='u-flex u-flex-gap u-margin-bottom--base' style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 280px', maxWidth: '280px' }}>
            <Select
              name='qId'
              className='o-select'
              clearable={false}
              searchable={false}
              value={parentSurvey.id}
              onChange={onSurveyChange}
              options={questionnaires}
            />
          </div>

          {withReport && (
            <div className='u-flex u-flex-gap' style={{ alignItems: 'flex-start' }}>
              <div className='u-flex'>
                <div>
                  <Select
                    name='locale'
                    className='o-select--prefixed'
                    clearable={false}
                    searchable={false}
                    disabled={download.isDownloading}
                    value={this.state.locale}
                    options={locales}
                    onChange={this.onLocaleChange}
                    optionRenderer={this.optionRenderer}
                    valueRenderer={this.optionRenderer}
                  />
                  <div className='c-locale-legend'><span role='img' aria-label='check'>✅</span> { 'Account\'s default language' }</div>
                </div>
                <button className={clsPDF} onClick={this.onClickASQPdf}>
                  {download.id === parentSurvey.id ?
                    <i className='o-spinner-dual'></i> :
                    <i className='fa fa-file-pdf-o'></i>}
                </button>
              </div>
              {asqFollowUpParentMessagesEnabled && isManageAsqFollowUp && (
                <div className='u-flex u-flex-column'>
                  <button
                    onClick={this.onClickFollowUpParentMessageOpen}
                    className="o-sp-button o-sp-button--base o-sp-button--primary o-sp-button--block c-send-parent-message-button"
                  >
                    <span className='u-txt--small'>
                      <b>
                        <i className='fa fa-envelope-o u-margin-right--small'></i>
                        Send Follow-Up Parent Message
                      </b>
                    </span>
                  </button>
                  <LastFollowUpParentMessageSendout survey={parentSurvey} />
                </div>
              )}

              {showEmailSendout && (
                <PlayerSurveyResultsParentEmailSendout
                  surveyId={parentSurvey.id}
                  resultsParentEmailSendout={asqSurveyResultsParentEmailSendout}
                  onEnqueue={this.onClickResultsParentEmailSendout}
                />
              )}

              {parentSurvey.state !== 'completed' && parentSurvey.state !== 'archived' && (
                <PlayerSurveyReminderSendout
                  surveyId={parentSurvey.id}
                />
              )}
            </div>
          )}
        </div>
        <div className='o-rtable o-rtable--bordered u-margin-bottom--gutter2x'>
          <div className='o-rtable__row'>
            <div className='o-player-cell u-flex u-flex-big-gap'>
              <div className='u-flex'>
                <div style={{ minWidth: '280px', paddingLeft: '30px' }} className='o-player-cell__select u-padding-left--zero'>
                  {'Assigned: ' + formatDate(parentSurvey.createdAt)}
                </div>
              </div>
            </div>
            {showTeacher &&
            <div className='o-player-cell o-player-cell__radio u-wspace--preline'>
              <span>{(teacherSurvey && teacherSurvey.respondentName) ? columnName(teacherSurvey.respondentName) : 'Teacher'}</span>
            </div>}
            <div className='o-player-cell o-player-cell__radio'>
              <span>{columnName(parentSurvey.respondentName)}</span>
            </div>
            <div className='o-player-cell o-player-cell__align'><span>{showTeacher ? 'Align' : 'Notes'}</span></div>
          </div>
          <div className='o-rtable__row o-rtable__row--bt-bordered'>
            <div className='o-player-cell c-header-xs'>{stateIcon(teacherSurvey || parentSurvey)}</div>
            {showTeacher &&
            <div className='o-player-cell o-player-cell__radio'>
              {teacherSurvey && teacherStateText(teacherSurvey, player.id, location, hasASQCompleteFeature(auth))}
            </div>}
            <div className='o-player-cell o-player-cell__radio'>{parentStateText(parentSurvey)}</div>
            <div className='o-player-cell o-player-cell__align'></div>
          </div>
          {rows}
          <div className='o-rtable__row o-rtable__row--bt-bordered'>
            <div className='o-player-cell__logo column'>
              <img alt={parentSurvey.type} src={ASQLogos[parentSurvey.type]}/>
            </div>
          </div>
        </div>
        <Modal
          classNames={{ overlay: 'o-modal__wrap', modal: 'o-modal o-modal__min-height row column u-margin-top--large' }}
          closeIconSvgPath={closeIconPath()}
          closeOnOverlayClick={false}
          open={this.state.followUpParentMessageOpen}
          onClose={this.onCloseFollowUpParentMessageOpen}
          center
        >
          <FollowUpParentMessage
            survey={parentSurvey}
            defaultLocale={this.props.player.locale}
            onSent={() => {
              this.setState({ hasUnsavedTranslation: false }, () => {
                this.onCloseFollowUpParentMessageOpen();
              });
            }}
            onTranslationStateChange={this.onTranslationStateChange}
          />
        </Modal>
      </Fragment>
    );
  }
}
